
import React, { useState, useRef } from 'react';
import { SimulationConfig, CustomMelody, MelodyData } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { AUDIO } from './services/AudioService';
import { CloudService } from './services/CloudService';
import { FileMusic, Upload, Trash2, Music } from 'lucide-react';

import { motion } from 'motion/react';

interface AudioWorkshopTabProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const AudioWorkshopTab: React.FC<AudioWorkshopTabProps> = ({ config, setConfig }) => {
  const [manualMelodyString, setManualMelodyString] = useState<string>("");
  const [melodyName, setMelodyName] = useState<string>("");
  const [detectedVoices, setDetectedVoices] = useState<string[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>("");
  const midiInputRef = useRef<HTMLInputElement>(null);

  const handleMidiUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const midiData = await AUDIO.parseMidiFile(file);
      const uploadedUrl = await CloudService.uploadFile(file);
      const tempId = Math.random().toString(36).substring(7);
      const newMelody: CustomMelody = {
        id: tempId,
        name: file.name.replace(/\.mid$/i, ''),
        frequencies: [],
        midiData,
        format: 'MIDI',
        url: uploadedUrl || undefined
      };
      
      const updatedConfig = {
        ...config,
        customMelodies: [...config.customMelodies, newMelody],
        selectedMelody: newMelody.id,
        // Reset filters for new song
        rhythmicMidiFilteredTracks: [],
        rhythmicMidiMutedTracks: [],
        rhythmicMidiBackgroundTracks: [],
        rhythmicMidiMinPitch: 0,
        rhythmicMidiMaxPitch: 127,
        rhythmicMidiMinVelocity: 0,
        rhythmicMidiMaxVelocity: 127,
        rhythmicMidiMinDuration: 0,
        rhythmicMidiMaxDuration: 10000,
        rhythmicMidiMuteBass: false,
        rhythmicMidiMuteMain: false
      };
      setConfig(updatedConfig);

      // Auto-save to cloud
      await CloudService.registerMelody(newMelody.name, newMelody);
      
      if (uploadedUrl) {
          await CloudService.registerSong(newMelody.name, uploadedUrl);
      }
      
      alert(`MIDI "${newMelody.name}" loaded and saved!`);
    } catch (err) {
      console.error("MIDI parse failed", err);
      alert("Failed to parse MIDI file.");
    }
    if (midiInputRef.current) midiInputRef.current.value = "";
  };

  const deleteMelody = async (id: string) => {
    // Optimistic update
    setConfig(prev => ({
      ...prev,
      customMelodies: prev.customMelodies.filter(m => m.id !== id),
      selectedMelody: prev.selectedMelody === prev.customMelodies.find(m => m.id === id)?.name ? 'Megalovania' : prev.selectedMelody
    }));
    
    // Cloud delete
    await CloudService.deleteMelody(id);
    // Also try to delete from songs if it was a MIDI upload
    await CloudService.deleteSong(id);
  };

  const applyManualMelody = async (save: boolean = false) => {
    const trimmed = manualMelodyString.trim();
    if (!trimmed) return;

    // Check if it's a MIDI base64 string
    // MIDI files start with MThd (TVRoZA in base64)
    if (trimmed.startsWith('TVRoZA') || trimmed.includes('data:audio/midi;base64,')) {
        try {
            const midiData = AUDIO.parseMidiBase64(trimmed, melodyName || 'Pasted MIDI');
            const tempId = Math.random().toString(36).substring(7);
            const newMelody: CustomMelody = {
                id: tempId,
                name: (melodyName || 'Pasted MIDI').trim(),
                frequencies: [],
                midiData,
                format: 'MIDI'
            };
            
            const updatedConfig = {
                ...config,
                customMelodies: [...config.customMelodies, newMelody],
                selectedMelody: newMelody.id,
                // Reset filters for new song
                rhythmicMidiFilteredTracks: [],
                rhythmicMidiMutedTracks: [],
                rhythmicMidiBackgroundTracks: [],
                rhythmicMidiMinPitch: 0,
                rhythmicMidiMaxPitch: 127,
                rhythmicMidiMinVelocity: 0,
                rhythmicMidiMaxVelocity: 127,
                rhythmicMidiMinDuration: 0,
                rhythmicMidiMaxDuration: 10000,
                rhythmicMidiMuteBass: false,
                rhythmicMidiMuteMain: false
            };
            setConfig(updatedConfig);

            if (save) {
                await CloudService.registerMelody(newMelody.name, newMelody);
                alert(`MIDI "${newMelody.name}" saved to Cloud & Library!`);
            }
            
            setMelodyName("");
            setManualMelodyString("");
            return;
        } catch (e) {
            console.error("Pasted MIDI parse failed", e);
        }
    }

    // Check if it's Online Sequencer text
    if (trimmed.startsWith('Online Sequencer:')) {
        try {
            const midiData = AUDIO.parseOnlineSequencerString(trimmed);
            const tempId = Math.random().toString(36).substring(7);
            const newMelody: CustomMelody = {
                id: tempId,
                name: (melodyName || 'Online Sequencer Song').trim(),
                frequencies: [],
                midiData,
                format: 'MIDI'
            };
            
            const updatedConfig = {
                ...config,
                customMelodies: [...config.customMelodies, newMelody],
                selectedMelody: newMelody.id,
                // Reset filters for new song
                rhythmicMidiFilteredTracks: [],
                rhythmicMidiMutedTracks: [],
                rhythmicMidiBackgroundTracks: [],
                rhythmicMidiMinPitch: 0,
                rhythmicMidiMaxPitch: 127,
                rhythmicMidiMinVelocity: 0,
                rhythmicMidiMaxVelocity: 127,
                rhythmicMidiMinDuration: 0,
                rhythmicMidiMaxDuration: 10000,
                rhythmicMidiMuteBass: false,
                rhythmicMidiMuteMain: false
            };
            setConfig(updatedConfig);

            if (save) {
                await CloudService.registerMelody(newMelody.name, newMelody);
                alert(`Song "${newMelody.name}" saved to Cloud & Library!`);
            }
            
            setMelodyName("");
            setManualMelodyString("");
            return;
        } catch (e) {
            console.error("Online Sequencer parse failed", e);
        }
    }

    const voices = AUDIO.getAvailableVoices(manualMelodyString);
    let freqs: MelodyData = [];
    
    if (voices.length > 0) {
        setDetectedVoices(voices);
        setSelectedVoice(voices[0]);
        freqs = AUDIO.parseMelodyString(manualMelodyString, voices[0]);
    } else {
        setDetectedVoices([]);
        setSelectedVoice("");
        freqs = AUDIO.parseMelodyString(manualMelodyString);
    }

    if (freqs.length > 0) {
        if (save && melodyName.trim()) {
            const tempId = Math.random().toString(36).substring(7);
            const newMelody: CustomMelody = {
                id: tempId,
                name: melodyName.trim(),
                frequencies: freqs,
                format: 'FREQUENCIES'
            };
            
            // 1. Update Local Config First (Optimistic)
            const updatedConfig = {
                ...config,
                customMelodies: [...config.customMelodies, newMelody],
                selectedMelody: newMelody.name
            };
            setConfig(updatedConfig);

            // 2. Save to Cloud Instantly
            try {
                // A. Register Melody
                const melodyRes = await CloudService.registerMelody(newMelody.name, newMelody);
                const cloudMelodyId = melodyRes?.id || tempId;

                // B. Register/Get Sound IDs
                const soundIds: string[] = []; 
                // Iterate through user sounds and register them if they have content
                for (const sound of config.userSounds) {
                    if (sound.url || sound.base64) {
                        const soundData = sound.url || sound.base64 || "";
                        // Try to register sound data
                        const soundRes = await CloudService.registerSoundData(sound.name, soundData);
                        if (soundRes && soundRes.id) {
                            soundIds.push(soundRes.id);
                        }
                    }
                }

                // C. Create a Full Save
                await CloudService.saveConfig(
                    newMelody.name, 
                    updatedConfig, 
                    false, 
                    cloudMelodyId, 
                    soundIds
                );
                
                alert(`Melody "${melodyName}" saved to Cloud & Library!`);
            } catch (e) {
                console.error("Cloud save failed", e);
                alert(`Saved locally, but Cloud sync failed.`);
            }

            setMelodyName("");
        } else {
            setConfig(prev => ({ ...prev, customFrequencies: freqs, selectedMelody: 'Custom (Uploaded)' }));
        }
        AUDIO.resetIndex();
        AUDIO.playFullSequence(freqs.slice(0, 5), 0.15);
    }
  };

  const handleRandomize = () => {
    const freqs = AUDIO.generateRandomMelody(16);
    setManualMelodyString(freqs.map(f => {
        if (typeof f === 'number') return Math.round(f);
        return 0; 
    }).join(", "));
    setConfig(prev => ({ ...prev, customFrequencies: freqs, selectedMelody: 'Custom (Uploaded)' }));
    AUDIO.resetIndex();
    AUDIO.playFullSequence(freqs.slice(0, 8), 0.12);
  };

  const updateVoiceFilter = (v: string) => {
    setSelectedVoice(v);
    const freqs = AUDIO.parseMelodyString(manualMelodyString, v);
    setConfig(prev => ({ ...prev, customFrequencies: freqs, selectedMelody: 'Custom (Uploaded)' }));
    AUDIO.resetIndex();
  };

  return (
    <div className={`flex flex-col gap-6 p-6 rounded-3xl border ${config.darkMode ? 'bg-orange-500/5 border-orange-500/10' : 'bg-orange-50 border-orange-100 shadow-lg'}`}>
      <div className="flex flex-col gap-3">
        <SectionLabel icon="fa-guitar" darkMode={config.darkMode}>Manual Melody Workshop</SectionLabel>
        
        <div className={`flex flex-col gap-5 p-5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
          <ControlGroup label="Naming Label" settingKey="melodyName" darkMode={config.darkMode}>
            <input 
              type="text" 
              value={melodyName}
              onChange={e => setMelodyName(e.target.value)}
              placeholder="Enter unique name..."
              className={`w-full px-4 py-2.5 rounded-2xl text-[10px] font-black outline-none transition-all border ${config.darkMode ? 'bg-black/40 border-white/10 text-white placeholder-white/20' : 'bg-neutral-50 border-neutral-200 text-neutral-800 placeholder-neutral-400 focus:border-orange-500/50 shadow-inner'}`}
            />
          </ControlGroup>

          <ControlGroup label="Sequence Matrix" settingKey="manualMelodyString" darkMode={config.darkMode}>
            <textarea 
              value={manualMelodyString} 
              onChange={e => setManualMelodyString(e.target.value)}
              className={`w-full h-32 p-4 rounded-2xl text-[11px] font-mono outline-none transition-all border leading-relaxed resize-none ${config.darkMode ? 'bg-black/60 border-white/10 text-orange-400 placeholder-white/10' : 'bg-neutral-900 border-neutral-800 text-orange-500 placeholder-neutral-700 shadow-inner'}`}
              placeholder={`Paste data stream here...\n\nExamples:\n• [261, 329, 392]\n• JSON Sequence\n• MIDI Base64\n• Online Sequencer Export`}
            />
          </ControlGroup>
          
          {detectedVoices.length > 0 && (
                <div className="flex flex-col gap-2">
                    <span className="text-[10px] font-bold uppercase opacity-100 text-orange-500/80">Voice Isolation Active</span>
                    <div className="flex flex-wrap gap-2">
                    {detectedVoices.map(v => (
                        <button key={v} onClick={() => updateVoiceFilter(v)} className={`px-4 py-1.5 rounded-xl text-[10px] font-black border transition-all ${selectedVoice === v ? 'bg-orange-600 text-white border-orange-500 shadow-lg shadow-orange-500/20 active:scale-95' : (config.darkMode ? 'bg-white/5 border-white/10 text-white/40' : 'bg-white border-neutral-100 text-neutral-400')}`}>VOICE {v}</button>
                    ))}
                    </div>
                </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => applyManualMelody(false)} className="w-full py-3.5 bg-neutral-600 text-white rounded-2xl font-black text-[10px] uppercase hover:bg-neutral-700 active:scale-95 transition-all shadow-lg shadow-black/10">Quick Test</button>
            <button onClick={() => applyManualMelody(true)} className="w-full py-3.5 bg-orange-600 text-white rounded-2xl font-black text-[10px] uppercase hover:bg-orange-700 active:scale-95 transition-all shadow-lg shadow-orange-500/20 disabled:opacity-40 disabled:grayscale" disabled={!melodyName.trim()}>Commit & Save</button>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col gap-3">
        <SectionLabel icon="fa-file-audio" darkMode={config.darkMode}>Hardware MIDI Port</SectionLabel>
        
        <input 
          type="file" 
          accept=".mid,.midi" 
          className="hidden" 
          ref={midiInputRef}
          onChange={handleMidiUpload}
        />
        
        <button 
          onClick={() => midiInputRef.current?.click()}
          className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-black text-[11px] uppercase hover:bg-emerald-700 active:scale-95 transition-all shadow-xl shadow-emerald-500/20 flex items-center justify-center gap-3 border border-emerald-500/30"
        >
          <Upload size={16} strokeWidth={3} />
          Import MIDI File
        </button>
      </div>

      <div className="flex flex-col gap-4">
        <SectionLabel icon="fa-list" darkMode={config.darkMode}>Silicon Sound Library</SectionLabel>
        
        <div className={`flex flex-col gap-3 max-h-80 overflow-y-auto p-4 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5 shadow-inner' : 'bg-neutral-100 border-neutral-200'} custom-scrollbar`}>
          {config.customMelodies.length === 0 && (
              <div className="py-12 text-center flex flex-col items-center gap-3 opacity-20">
                <Music size={32} />
                <span className="text-[10px] font-black uppercase tracking-widest">Library Empty</span>
              </div>
          )}
          {config.customMelodies.map(m => (
              <motion.div 
                initial={{ opacity: 0, x: -10 }} 
                animate={{ opacity: 1, x: 0 }} 
                key={m.id} 
                className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${config.darkMode ? 'bg-black/40 border-white/5 shadow-sm' : 'bg-white border-neutral-100 shadow-sm'}`}
              >
                  <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl shadow-inner ${m.format === 'MIDI' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-orange-500/10 text-orange-500'}`}>
                          {m.format === 'MIDI' ? <FileMusic size={16} /> : <Music size={16} />}
                      </div>
                      <div className="flex flex-col">
                          <span className="text-[11px] font-black truncate max-w-[150px]">{m.name}</span>
                          <span className={`text-[8px] font-black uppercase tracking-wider opacity-60 ${m.format === 'MIDI' ? 'text-emerald-500' : 'text-orange-500'}`}>
                              {m.format || 'LEGACY'} • {m.format === 'MIDI' ? `${m.midiData?.notes.length} NOTES` : `${m.frequencies.length} STEPS`}
                          </span>
                      </div>
                  </div>
                  <div className="flex items-center gap-2">
                      <button 
                          onClick={() => setConfig(p => ({ ...p, selectedMelody: m.name }))}
                          className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all active:scale-95 ${config.selectedMelody === m.name ? 'bg-orange-600 text-white shadow-lg shadow-orange-500/20' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'}`}
                          disabled={config.mode !== 'RHYTHMIC'}
                      >
                          {config.selectedMelody === m.name ? 'Active' : 'Load'}
                      </button>
                      <button 
                          onClick={() => deleteMelody(m.id)}
                          className="p-2 text-red-500 hover:bg-red-500/10 rounded-xl transition-all active:scale-90"
                      >
                          <Trash2 size={16} />
                      </button>
                  </div>
              </motion.div>
          ))}
        </div>
      </div>

      <button 
        onClick={handleRandomize} 
        className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-[11px] uppercase hover:bg-indigo-700 active:scale-95 transition-all shadow-xl shadow-indigo-500/20 border border-indigo-500/30"
      >
        <i className="fa-solid fa-dice mr-2"></i> Randomize Silicon Buffer
      </button>
    </div>
  );
};
